import { ENV } from '../constants/environments';
import { LICENSE_IDS } from '../constants/api';
import { BUNDLES_INDEX_DEFAULT_FILTER, BUNDLE_FILTER_KEYS } from '../constants/ui';
const { LIBRARY_ID } = BUNDLE_FILTER_KEYS;

import { QueryParams } from '../types/api/BasicTypes';
import { User } from '../types/api/UsersTypes';
import { SubscriptionDetail } from '../types/api/SubscriptionTypes';
import { SessionState } from '../types/state/reducers/authReducersTypes';
import { BundlesListServersideProps } from '../types/pages/bundles/index';
import { BundlePageServersideProps } from '../types/pages/bundles/id';
import { BundleMinimalView, BundleView, BundleCollectionMinimalView } from '../types/api/BundlesTypes';
import { LicenseId } from '../types/api/LicenseTypesTypes';
import { CategoryResponse } from '../types/api/CategoriesIndexTypes';
import { ServerSideProps } from '../types/pages/generic/serverSideProps';
import { RedirectConfigProps } from '../types/pages/generic/redirectConfig';
import { PagedResponse } from '../types/api/Http';
import { MultipleImageThumbnailProps } from '../types/components/shared/MultipleImageThumbnail';
import { GetServerSidePropsContext } from 'next';

import axios, { AxiosError, AxiosResponse } from 'axios';
import errorRedirectHandler from '../helpers/errorRedirectHandler';
import categoriesHelpers from './categoriesHelpers';

export const bundlesUrl =  `${ENV.api.baseURL}/bundles`;
export const cacheExpirySecs = 3600;
export const categorySortingFunction = (a: CategoryResponse, b: CategoryResponse) => a.name.localeCompare(b.name);

export const DEFAULT_BUNDLES_INDEX_QUERY_PARAMS = {
  [BUNDLES_INDEX_DEFAULT_FILTER.queryParamKey]: BUNDLES_INDEX_DEFAULT_FILTER.queryParamValue,
};

const fetchBundlesFromBackend = async (
  user: User | undefined,
  params: QueryParams = DEFAULT_BUNDLES_INDEX_QUERY_PARAMS,
): Promise<PagedResponse<BundleMinimalView>> => {
  const headers = {
    authorization: user?.token,
  };
  const result: AxiosResponse<PagedResponse<BundleMinimalView>> = await axios.get(bundlesUrl, { params, headers });
  return result.data;
};

const getBundlesListServersideProps = async (
  ctx: GetServerSidePropsContext,
  session: SessionState,
  library_id: number,
): Promise<ServerSideProps<BundlesListServersideProps> | RedirectConfigProps> => {
  ctx.res.setHeader('Cache-Control', 'no-store, no-cache, must-revalidate, proxy-revalidate');
  const user = session?.user as User;
  const { fetchBundlesFromBackend } = bundlesHelpers;
  const { getCategories } = categoriesHelpers;

  let categories: Array<CategoryResponse>;
  let bundles: PagedResponse<BundleMinimalView>;

  const params = {
    ...DEFAULT_BUNDLES_INDEX_QUERY_PARAMS,
    [LIBRARY_ID]: library_id,
  };
  const getBundlesPromise = fetchBundlesFromBackend(user, params);
  const getCategoriesPromise = getCategories(library_id);
  let promisesError: AxiosError;

  const promises = [getBundlesPromise, getCategoriesPromise];
  await Promise.all(promises)
  .then((responses: Array<PagedResponse<BundleMinimalView> | CategoryResponse[]>) => {
    bundles = responses[0] as PagedResponse<BundleMinimalView>;
    categories = (responses[1] as CategoryResponse[]).sort(categorySortingFunction);
  })
  .catch((error: AxiosError) => {
    console.log(error);
    promisesError = error;
  });

  if (promisesError) return errorRedirectHandler(promisesError);

  return {
    props: {
      bundles,
      categories,
    },
  };
};

const fetchBundleDetailsFromBackend = async (
  slug: string,
  user: User | undefined,
): Promise<BundleView> => {
  const headers = {
    authorization: user?.token,
  };
  const result: AxiosResponse<BundleView> = await axios.get(`${bundlesUrl}/${slug}`, { headers });
  return result.data;
};

const getBundleDetailsPageServersideProps = async (
  ctx: GetServerSidePropsContext,
  session: SessionState,
): Promise<ServerSideProps<BundlePageServersideProps> | RedirectConfigProps> => {
  ctx.res.setHeader('Cache-Control', 'no-store, no-cache, must-revalidate, proxy-revalidate');
  const user = session?.user as User;
  const slug = ctx.query.slug as string;
  const { fetchBundleDetailsFromBackend } = bundlesHelpers;

  let bundle: BundleView;
  const getBundleDetailsPromise = fetchBundleDetailsFromBackend(slug, user);

  let promisesError: AxiosError;

  // in case more async calls will come in here, which they most likely will
  const promises = [getBundleDetailsPromise];
  await Promise.all(promises)
  .then((responses: Array<BundleView>) => {
    bundle = responses[0] as BundleView;
  })
  .catch((error: AxiosError) => {
    console.log(error);
    promisesError = error;
  });

  if (promisesError) return errorRedirectHandler(promisesError);

  return {
    props: {
      bundle,
    },
  };
};

const bundleCollectionIsOwned = (collection: BundleCollectionMinimalView): boolean => {
  return collection.fully_purchased === true;
};

const updateBundleOwnershipState = (bundle: BundleView, owned: boolean): BundleView => {
  const mappedCollections = bundle.collections.map(
    c => ({ ...c, fully_purchased: owned }),
  );
  return { ...bundle, owned, collections: mappedCollections };
};

const isolateBundleNameParts = (bundleName: string): { isolatedBundleName: string, bundleWord: string } => {
  if (!bundleName) return { isolatedBundleName: 'Bundle', bundleWord: '' };

  const bundleRegex = /\s*(bundle)\s*$/i;
  const match = bundleName.match(bundleRegex);

  if (match) {
    const isolatedBundleName = bundleName.replace(bundleRegex, '').trim();
    return { isolatedBundleName, bundleWord: match[1] };
  }

  return { isolatedBundleName: bundleName, bundleWord: '' };
};

const mapBundleCollectionThumbnailsToMultipleImageThumbnailProps = (bundle: BundleView, className?: string): MultipleImageThumbnailProps => {
  const props: MultipleImageThumbnailProps = { media: {} };
  if (className) props.className = className;
  bundle.collections
  .filter(c => !!c.media.image)
  .slice(0, 4)
  .forEach((c, index) => {
    const key = `link${index + 1}`;
    props.media[key] = c.media.image;
  });
  return props;
};

const getBundleDiscountPercent = (bundle: BundleMinimalView | BundleView): string => {
  return Math.floor(bundle.effective_discount * 100).toFixed(0);
};

const shouldDisplayPriceForIndividualsMarker = (
  authenticated: boolean,
  user: User,
  subscription: SubscriptionDetail,
): boolean => {
  if (authenticated === undefined) return false;
  if (authenticated === false) return true;
  return !subscription && !user.min_license_id;
};

const shouldUpdateBundlePriceInfo = (
  previousMinLicenseId: undefined | null | LicenseId,
  nextMinLicenseId: undefined | null | LicenseId,
): boolean => {
  const { INDIVIDUAL } = LICENSE_IDS;
  // A minimal license id may have "undefined" or "null" value based on
  // whether the user session is loaded, is anonymous or is authenticated,
  // but as far as price reloading is concerned, it does not make a difference,
  // we do not reload prices if we don't know both the previous and next values.
  if (!previousMinLicenseId && !nextMinLicenseId) return false;
  // When minimal license id is not known we show individual prices anyway,
  // so no need to reload prices if it changes from "unknown" to "individual" or vice versa.
  if (
    !previousMinLicenseId && nextMinLicenseId === INDIVIDUAL ||
    previousMinLicenseId === INDIVIDUAL && !nextMinLicenseId
  ) return false;
  // After having covered the edge cases, simply return whether the values are different
  return previousMinLicenseId !== nextMinLicenseId;
};

const bundlesHelpers = {
  fetchBundlesFromBackend,
  getBundlesListServersideProps,
  fetchBundleDetailsFromBackend,
  getBundleDetailsPageServersideProps,
  bundleCollectionIsOwned,
  updateBundleOwnershipState,
  isolateBundleNameParts,
  mapBundleCollectionThumbnailsToMultipleImageThumbnailProps,
  getBundleDiscountPercent,
  shouldDisplayPriceForIndividualsMarker,
  shouldUpdateBundlePriceInfo,
};

export default bundlesHelpers;
